import { CreatorContentMonitor } from "./creatorContentMonitoring";
import { MemberWithNotificationType, MetafieldAccessMode } from "./meta-field";
import { XtrmPaymentsDetails } from "./payments";

export enum ExtensionTypeEnum {
  CreatorDatabaseSubmissionForm = "creatorDatabaseForm",
  CreatorDatabaseMetaFieldList = "creatorDatabaseMetaFieldList",
  CreatorDatabaseMetaFieldMembers = "creatorDatabaseMetaFieldMembers",
  RecruitmentForm = "recruitmentForm",
  WsProjectLock = "wsProjectLock",
  ApiConnection = "apiConnection",
  XtrmPayments = "xtrmPayments",
  CreatorMessenger = "messenger",
  CreateSingleAndSplitPayments = "createSingleAndSplitPayments",
  StorePublishedContent = "storePublishedContents",
  CreatorContentMonitoring = "creatorContentMonitoring",
}

type XtrmPaymentsSettings = {
  BeneficiaryID: string;
};

type CreatorMessengerSettings = {
  encryption: string | null;
  fromEmail: string | null;
  fromName: string | null;
  host: string | null;
  login: string | null;
  password: string | null;
  port: number | null;
  wsGlobalTaskMetaFieldUuidWithEmail: string | null;
  dataProcessingInformation: string | null;
};

type CreatorDatabaseSubmissionFormSettings = {
  token: string;
  wsGlobalMetaFieldUuid: string;
};

type RecruitmentFormDetailsSettings = {
  token: string;
  wsTaskMetaFieldUuid: string;
};

type WsExtensionBase = {
  enabled: boolean;
  uuid: string;
  relatedUuid: string | null;
  wsWorkspaceUuid: string | null;
  wsProjectUuid: string | null;
};

export type WsExtensionCreatorDatabaseMetaFieldList = {
  type: ExtensionTypeEnum.CreatorDatabaseMetaFieldList;
} & WsExtensionBase;

export type WsExtensionXtrmPayments = {
  type: ExtensionTypeEnum.XtrmPayments;
  settings: XtrmPaymentsSettings;
} & WsExtensionBase;

export type WsExtensionCreatorMessenger = {
  type: ExtensionTypeEnum.CreatorMessenger;
  settings: CreatorMessengerSettings;
} & WsExtensionBase;

export type WsExtensionRecruitmentForm = {
  type: ExtensionTypeEnum.RecruitmentForm;
  settings: RecruitmentFormDetailsSettings;
} & WsExtensionBase;

export type WsExtensionCreatorDatabaseSubmissionForm = {
  type: ExtensionTypeEnum.CreatorDatabaseSubmissionForm;
  settings: CreatorDatabaseSubmissionFormSettings;
} & WsExtensionBase;

export type WsExtensionWsProjectLock = {
  type: ExtensionTypeEnum.WsProjectLock;
  settings: [];
} & WsExtensionBase;

export type WsApiConnection = {
  type: ExtensionTypeEnum.ApiConnection;
  settings: {
    brandName: string;
    encodedData: string;
    token: string;
  };
} & WsExtensionBase;

export type WsExtensionCreateSingleAndSplitPayments = {
  type: ExtensionTypeEnum.CreateSingleAndSplitPayments;
  settings: {
    amountLocalValueGlobalTaskMetaFieldUuid: string;
    amountValueLocalTaskMetaFieldUuid: string;
    assigneeLocalTaskMetaFieldUuid: string;
    dueDateLocalTaskMetaFieldUuid: string;
    formulaFieldGlobalTaskMetaFieldUuid: string;
    wsMemberUuidToAssigneePayments: string;
  };
} & WsExtensionBase;

export type WsExtensionCreatorContentMonitoring = {
  type: ExtensionTypeEnum.CreatorContentMonitoring;
  settings: {
    monitors: CreatorContentMonitor[];
  };
} & WsExtensionBase;

export type WsExtensionStorePublishedContent = {
  type: ExtensionTypeEnum.StorePublishedContent;
  settings: [];
} & WsExtensionBase;

export type WsExtension =
  | WsExtensionXtrmPayments
  | WsExtensionRecruitmentForm
  | WsExtensionWsProjectLock
  | WsExtensionCreatorDatabaseSubmissionForm
  | WsExtensionCreatorDatabaseMetaFieldList
  | WsApiConnection
  | WsExtensionCreatorMessenger
  | WsExtensionCreateSingleAndSplitPayments
  | WsExtensionCreatorContentMonitoring
  | WsExtensionStorePublishedContent;

export type WsExtensionCreatorMessengerDetails = CreatorMessengerSettings & {
  wsWorkspaceUuid: string;
  isEnabled: boolean;
  uuid: string;
  type: ExtensionTypeEnum.CreatorMessenger;
};

export type GlobalMetaFieldListItemData = {
  uuid: string;
  name: string;
  members: [];
};

export type GlobalMetaFieldDetails = {
  uuid: string;
  wsGlobalTaskMetaFieldUuid: string | null;
  members: string[];
  name: string;
};

export type RecruitmentFormDetailsAsset = {
  parentUuid: string; // extension uuid
  name: string;
  mimeType: string;
  realName: string;
  size: number;
};

export type RecruitmentFormUserDataMembersWithNotification = {
  wsMemberUuid: string | null;
  wsTeamUuid: string | null;
  type: MemberWithNotificationType;
};

export type RecruitmentFormDetails = {
  uuid: string;
  wsProjectUuid: string | null;
  wsWorkspaceUuid: string | null;
  type: string;
  settings: RecruitmentFormDetailsSettings;
  enabled: boolean;
  recruitmentFormUuid: string;
  recruitmentFormMetaFields: RecruitmentFormMetaField[];
  recruitmentFormMetaFieldsAvailable: RecruitmentFormMetaFieldAvailable[];
  recruitmentFormCaption: string;
  recruitmentFormDescription: string;
  recruitmentFormSuccessMessage: string;
  recruitmentIframeHtml: string;
  recruitmentFormUrl: string;
  wsAccessLinkLogo: RecruitmentFormDetailsAsset | string | null;
  wsAccessLinkCover: RecruitmentFormDetailsAsset | string | null;
  recruitmentFormMembersWithNotification: RecruitmentFormUserDataMembersWithNotification[];
};

type AllowedMetaFieldsValues = {
  [key: string]: string[];
};

export type SingleAndSplitPaymentDetails = {
  type: ExtensionTypeEnum.CreateSingleAndSplitPayments;
  uuid: string;
  isEnabled: boolean;
  wsProjectUuid: string;
  paymentCreatorAmountGlobalTaskMetaFieldUuid: string;
  paymentAmountPercentGlobalTaskMetaFieldUuid: string;
  paymentDueDateGlobalTaskMetaFieldUuid: string;
  paymentAmountGlobalTaskMetaFieldUuid: string;
  paymentFileGlobalTaskMetaFieldUuid: string;
  paymentFileLocalTaskMetaFieldUuid: string;
  paymentFileLocalTaskMetaFieldName: string | null;
  allowedMetaFieldsValues: AllowedMetaFieldsValues[];
};

export type ExtensionDetails =
  | RecruitmentFormDetails
  | XtrmPaymentsDetails
  | SingleAndSplitPaymentDetails;

export type RecruitmentFormMetaFieldAvailable = {
  uuid: string;
  type: string;
  key: string;
  name: string;
  isVisible: boolean;
  isRequired: boolean;
  wsMetaFieldDescription: string | null;
};

export type RecruitmentFormMetaField = {
  wsMetaFieldName: string;
  wsMetaFieldType: string;
  wsMetaFieldUuid: string;
  wsMetaFieldAccessMode: MetafieldAccessMode;
  wsMetaFieldIsRequired: boolean;
  wsMetaFieldIsNotDeletable: boolean;
  wsMetaFieldDescription: string | null;
};
